import React, { useEffect, useMemo } from 'react';

import { Avatar, Box, Chip, CircularProgress, makeStyles, Theme, createStyles } from '@material-ui/core';
import { FiFileText } from 'react-icons/fi';

import { bytesToSize } from '../../../utils/fileSize';

export interface ChatComposerFile {
  key: string;
  name: string;
  size?: number;
  // A picture shows as a thumbnail.
  image?: File;
  uploading?: boolean;
  onRemove?: () => void;
}

const Thumbnail = ({ file }: { file: File }) => {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return <Avatar src={url} variant="rounded" alt="" />;
};

// Files waiting to go with the next message, over the input.
export const ChatComposerFiles: React.FC<{ files: ChatComposerFile[] }> = ({ files }) => {
  const classes = useStyles();

  if (!files.length) {
    return null;
  }

  return (
    <Box className={classes.root}>
      {files.map(file => (
        <Chip
          key={file.key}
          size="small"
          label={`${file.name} · ${file.uploading ? 'uploading' : file.size ? bytesToSize(file.size) : ''}`}
          avatar={file.image ? <Thumbnail file={file.image} /> : undefined}
          icon={
            file.image ? undefined : file.uploading ? (
              <CircularProgress size={12} />
            ) : (
              <FiFileText size={14} />
            )
          }
          onDelete={file.uploading ? undefined : file.onRemove}
          className={classes.file}
        />
      ))}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { display: 'flex', flexWrap: 'wrap', gap: 6, padding: '2px 4px 0' },
    file: { maxWidth: '100%', '& .MuiChip-icon': { color: theme.palette.text.secondary } },
  })
);
